import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { ChatBubbleLeftIcon } from '@heroicons/react/24/solid'
import { contextTypeColorMap } from '../../constants/contextTypeColorMap'
import { formatMessageTimestamp } from '../../utils/dateFormat'
import PageTitle from '../../components/common/PageTitle'
import USER_TYPE from '../../constants/userType'
import DisplayShopName from '../../components/common/DisplayShopName'
import { MESSAGE_LISTING_REFETCH_INTERVAL_MILLIS } from '../../constants/messagingConstants'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { useFilters } from '../../hooks/useFilters'

const MessageInboxPage = () => {
  const { currentUser, userType } = useAuth()
  const navigate = useNavigate()
  const {
    filters: { page },
    renderPagination,
  } = useFilters()

  const senderId = currentUser?.id

  const { data: conversationsData, isLoading } = useQuery({
    queryKey: QueryKeys.listMessages({
      userType,
      page,
    }),
    queryFn: () => handleGetRequest(`/${userType}s/messages`),
    enabled: !!senderId,
    refetchInterval: MESSAGE_LISTING_REFETCH_INTERVAL_MILLIS,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    )
  }

  const conversations = conversationsData?.data || []

  const renderName = (user) => {
    if ([USER_TYPE.retailer, USER_TYPE.wholeseller].includes(user?.userType)) {
      return (
        <DisplayShopName
          shop={{
            name: user?.shopName,
            isPremium: user?.isPremium,
            isVerified: user?.isApproved,
          }}
        />
      )
    }

    return user?.name
  }

  return (
    <div className="main-container min-h-screen">
      <PageTitle>Messages</PageTitle>
      {conversations.length === 0 ? (
        <div className="text-center text-gray-500 mt-16">
          <ChatBubbleLeftIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">You have no conversations yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {conversations.map((convo) => (
            <div
              key={convo.conversationId}
              onClick={() =>
                navigate(`/messages/${convo.conversationId}`, {
                  state: {
                    conversationId: convo.conversationId,
                    targetId: convo.otherUser.id,
                    contextType: convo.contextType,
                    contextId: convo.contextId,
                  },
                })
              }
              className="flex justify-between items-start bg-background p-4 rounded-xl shadow-sm border hover:bg-neutral-200-50 cursor-pointer transition"
            >
              <div className="flex-1 min-w-0 font-fontFamily">
                <p className="font-medium text-lg text-black">{renderName(convo.otherUser)}</p>
                <p className="text-[15px] text-gray-600 truncate">{convo.latestMessage}</p>
              </div>
              <div className="text-right text-sm text-gray-400 whitespace-nowrap ml-3 font-fontFamily">
                <p
                  className={`font-bold mt-1 text-sm text-[10px] tracking-wide
                  text-${contextTypeColorMap[convo.contextType] || contextTypeColorMap.Default}`}
                >
                  {convo.contextType}
                </p>
                <p className="mt-1 text-[15px] font-normal">
                  {formatMessageTimestamp(convo.latestTimestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      {renderPagination(conversationsData?.meta)}
    </div>
  )
}

export default MessageInboxPage
